import { useMutation, useQueryClient } from "@tanstack/react-query";
import { qk } from "@/lib/query-keys";
import { toastAdvertencia, toastErrorApi, toastExito } from "@/lib/toast";
import {
  cambiarEstadoClaveSatClaveCuenta,
  crearClaveSatClaveCuenta,
  editarClaveSatClaveCuenta,
  eliminarMasivoClavesSatClavesCuenta,
  importarClavesSatClavesCuenta,
  type CrearClaveSatClaveCuentaInput,
  type EditarClaveSatClaveCuentaInput,
  type FilaImportacionClaveSatClaveCuenta,
} from "../services/clave-sat-clave-cuenta-service";

function invalidar(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: qk.adminClavesSatClavesCuenta });
}

export function useCrearClaveSatClaveCuenta(opts?: { onCreado?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CrearClaveSatClaveCuentaInput) => crearClaveSatClaveCuenta(input),
    onSuccess: () => {
      toastExito({ title: "Mapeo creado", message: "La clave SAT ya deriva su cuenta contable en el prorrateo." });
      invalidar(qc);
      opts?.onCreado?.();
    },
    onError: toastErrorApi,
  });
}

export function useEditarClaveSatClaveCuenta(opts?: { onEditado?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: EditarClaveSatClaveCuentaInput }) => editarClaveSatClaveCuenta(id, input),
    onSuccess: () => {
      toastExito({ title: "Mapeo actualizado", message: "Cambios guardados." });
      invalidar(qc);
      opts?.onEditado?.();
    },
    onError: toastErrorApi,
  });
}

export function useCambiarEstadoClaveSatClaveCuenta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, activo }: { id: string; claveSat: string; activo: boolean }) => cambiarEstadoClaveSatClaveCuenta(id, activo),
    onSuccess: (_data, vars) => {
      toastExito({
        title: vars.activo ? `"${vars.claveSat}" reactivado` : `"${vars.claveSat}" dado de baja`,
        message: vars.activo ? "Vuelve a derivar cuenta en el prorrateo." : "Deja de derivar cuenta · los prorrateos históricos conservan la suya.",
      });
      invalidar(qc);
    },
    onError: toastErrorApi,
  });
}

export function useEliminarMasivoClavesSatClavesCuenta(opts?: { onEliminado?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => eliminarMasivoClavesSatClavesCuenta(ids),
    onSuccess: (res) => {
      // Cuando alguna estaba en uso se desactiva (no se borra) · se avisa con un warning.
      const resumen = `${res.eliminados} eliminados${res.desactivados > 0 ? ` · ${res.desactivados} desactivados (en uso)` : ""}`;
      if (res.desactivados > 0) {
        toastAdvertencia({
          title: "Eliminación masiva parcial",
          message: `${resumen}. Las desactivadas ya derivaron una cuenta en algún prorrateo, así que se conservan dadas de baja.`,
        });
      } else {
        toastExito({ title: "Mapeos eliminados", message: resumen + "." });
      }
      invalidar(qc);
      opts?.onEliminado?.();
    },
    onError: toastErrorApi,
  });
}

export function useImportarClavesSatClavesCuenta(opts?: { onImportado?: () => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (filas: FilaImportacionClaveSatClaveCuenta[]) => importarClavesSatClavesCuenta(filas),
    onSuccess: (res) => {
      const partes = [`${res.creados} creados`, `${res.actualizados} actualizados`, `${res.omitidos} omitidos`];
      if (res.errores.length > 0) {
        // Mostramos hasta 5 errores en el toast · el resto se resume con un conteo.
        const muestra = res.errores.slice(0, 5).join(" · ");
        const extra = res.errores.length > 5 ? ` (+${res.errores.length - 5} más)` : "";
        toastAdvertencia({ title: `Importación con incidencias · ${partes.join(", ")}`, message: `${muestra}${extra}` });
      } else {
        toastExito({ title: "Importación completada", message: partes.join(", ") + "." });
      }
      invalidar(qc);
      opts?.onImportado?.();
    },
    onError: toastErrorApi,
  });
}
